// components/WordFileImporter.tsx
import React, { useState } from 'react';
import mammoth from 'mammoth';
import { Upload, FileText, Loader2 } from 'lucide-react';

interface WordFileImporterProps {
  onImport: (html: string) => void;
}

const WordFileImporter: React.FC<WordFileImporterProps> = ({ onImport }) => {
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier que c'est un fichier Word
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword' // .doc
    ];
    const validExtensions = ['.doc', '.docx'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      setError('Veuillez sélectionner un fichier Word (.doc ou .docx)');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      
      if (result.value) {
        onImport(result.value);
      } else {
        setError('Erreur lors de la conversion du fichier Word');
      }
    } catch (err: any) {
      console.error('Erreur import Word:', err);
      setError(err.message || 'Erreur lors de l\'import du fichier Word');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-4 border border-gray-300 rounded-lg bg-gray-50">
      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
        <div className="flex flex-col items-center justify-center pt-5 pb-6">
          {importing ? (
            <>
              <Loader2 className="w-10 h-10 mb-3 text-blue-600 animate-spin" />
              <p className="text-sm text-gray-600">Import en cours...</p>
            </>
          ) : (
            <>
              <Upload className="w-10 h-10 mb-3 text-gray-400" />
              <p className="mb-2 text-sm text-gray-500">
                <span className="font-semibold">Cliquez pour importer</span> un fichier Word
              </p>
              <p className="text-xs text-gray-500">DOC ou DOCX</p>
            </>
          )}
        </div>
        <input
          type="file"
          className="hidden"
          accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleFileSelect}
          disabled={importing}
        />
      </label>
      
      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
          {error}
        </div>
      )}
    </div>
  );
};

export default WordFileImporter;

